import React from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Platform } from 'react-native';
import { Icon } from './Icon';
import { colors, spacing, borderRadius, fontWeights, fonts } from '../theme';

export enum ColumnKey {
  POLICY_YEAR = 'policyYear',
  AGE = 'age',
  PREMIUM_PAID = 'premiumPaid',
  TOPUP_PAID = 'topUpPaid',
  INITIAL_CHARGE = 'initialCharge',
  POLICY_FEE = 'policyFee',
  COI = 'coi',
  ACCOUNT_VALUE = 'accountValue',
  SURRENDER_VALUE = 'surrenderValue',
  DEATH_BENEFIT = 'deathBenefit',
  LOYALTY_BONUS = 'loyaltyBonus',
  BASE_PREMIUM = 'basePremium',
  ALLOCATED_BASE_PREMIUM = 'allocatedBasePremium',
  ALLOCATED_PREMIUM = 'allocatedPremium',
  TOTAL_DEDUCTION = 'totalDeduction',
  STBH_MAIN_PRODUCT = 'stbhMainProduct',
  BASE_ACCOUNT_VALUE = 'baseAccountValue',
  TOPUP_ACCOUNT_VALUE = 'topUpAccountValue',
  INTEREST_EARNED = 'interestEarned',
  WITHDRAWAL_TOTAL = 'withdrawalTotal',
  RIDER_PREMIUM = 'riderPremium',
}

export interface ColumnInfoDetails {
  key: string;
  title: string;
  category: string;
  badgeColor?: string;
  shortSummary: string;
  description: string;
  feeSchedule?: Array<{ yearLabel: string; rateLabel: string; isZeroFee?: boolean }>;
  notes?: string[];
}

export const COLUMN_INFO_DATABASE: Record<string, ColumnInfoDetails> = {
  policyYear: {
    key: 'policyYear',
    title: 'Năm Hợp Đồng (Year)',
    category: 'Thời Gian',
    badgeColor: '#2563EB',
    shortSummary: 'Thứ tự năm tính từ ngày Hợp đồng bắt đầu có hiệu lực.',
    description:
      'Năm hợp đồng xác định thứ tự từng năm tính từ Ngày hiệu lực hợp đồng. Mỗi năm hợp đồng gồm 12 tháng phí. Các mốc tỷ lệ phí ban đầu, phí hủy hợp đồng và quyền lợi thưởng duy trì đều được tính dựa trên số năm hợp đồng này.',
  },
  age: {
    key: 'age',
    title: 'Tuổi Người Được Bảo Hiểm',
    category: 'Thông Tin NĐBH',
    badgeColor: '#2563EB',
    shortSummary: 'Độ tuổi thực tế của NĐBH chính tại năm hợp đồng tương ứng.',
    description:
      'Tuổi của Người được bảo hiểm chính tại thời điểm bắt đầu mỗi năm hợp đồng. Tuổi tác là căn cứ trực tiếp để tính Chi phí bảo hiểm rủi ro (COI) và phí của các sản phẩm bổ trợ bảo vệ sức khỏe (như Sống Khỏe Mỗi Ngày, Lá Chắn Xanh, Hộ Vệ Xanh).',
  },
  premiumPaid: {
    key: 'premiumPaid',
    title: 'Tổng Phí Bảo Hiểm Định Kỳ',
    category: 'Đóng Phí',
    badgeColor: '#00A651',
    shortSummary: 'Số tiền phí bảo hiểm định kỳ khách hàng đóng hàng năm.',
    description:
      'Tổng khoản phí định kỳ (bao gồm Phí cơ bản sản phẩm chính và Phí các sản phẩm bổ trợ) mà khách hàng cam kết đóng hàng năm theo kỳ đóng phí (Năm, Nửa năm, Quý) để duy trì hiệu lực hợp đồng.',
  },
  topUpPaid: {
    key: 'topUpPaid',
    title: 'Phí Bảo Hiểm Đóng Thêm (Top-Up)',
    category: 'Đầu Tư Thêm',
    badgeColor: '#0ea5e9',
    shortSummary: 'Khoản phí đầu tư thêm ngoài phí định kỳ để gia tăng tài sản.',
    description:
      'Phí đóng thêm là khoản tiền bổ sung linh hoạt khách hàng đóng thêm để đầu tư tích lũy tài sản. 100% khoản phí này được phân bổ thẳng vào Giá trị tài khoản đóng thêm mà không bị trừ phí ban đầu từ năm thứ nhất.',
    feeSchedule: [
      { yearLabel: 'Năm 1 trở đi', rateLabel: 'Không trừ phí ban đầu (100% đầu tư)', isZeroFee: true },
    ],
  },
  initialCharge: {
    key: 'initialCharge',
    title: 'Phí Ban Đầu (Initial Charge)',
    category: 'Chi Phí Khấu Trừ',
    badgeColor: '#EF4444',
    shortSummary: 'Khấu trừ từ phí cơ bản trước khi đưa vào tài khoản tích lũy.',
    description:
      'Phí ban đầu là khoản phí được trừ trực tiếp từ Phí bảo hiểm cơ bản thực đóng của sản phẩm chính trước khi số tiền còn lại được phân bổ vào Giá trị tài khoản hợp đồng để tích lũy lãi.',
    feeSchedule: [
      { yearLabel: 'Năm HĐ 1', rateLabel: '50% Phí cơ bản' },
      { yearLabel: 'Năm HĐ 2', rateLabel: '30% Phí cơ bản' },
      { yearLabel: 'Năm HĐ 3 - 5', rateLabel: '20% Phí cơ bản' },
      { yearLabel: 'Năm HĐ 6 - 10', rateLabel: '2% Phí cơ bản' },
      { yearLabel: '🎯 Từ Năm HĐ 11 trở đi', rateLabel: '0% — HOÀN TOÀN KHÔNG TỐN PHÍ BAN ĐẦU!', isZeroFee: true },
    ],
    notes: [
      'Từ năm thứ 11 trở đi, 100% phí bảo hiểm cơ bản đóng vào đều được đưa thẳng vào tài khoản tích lũy.',
      'Phí đầu tư thêm (Top-Up) không áp dụng biểu phí ban đầu này.',
    ],
  },
  policyFee: {
    key: 'policyFee',
    title: 'Phí Quản Lý Hợp Đồng (Policy Fee)',
    category: 'Chi Phí Khấu Trừ',
    badgeColor: '#F59E0B',
    shortSummary: 'Khấu trừ hàng tháng từ tài khoản để quản lý dịch vụ HĐ.',
    description:
      'Phí quản lý hợp đồng được khấu trừ tự động hàng tháng từ Giá trị tài khoản hợp đồng để phục vụ công tác quản lý, phát hành hóa đơn và chăm sóc hợp đồng.',
    feeSchedule: [
      { yearLabel: 'Năm HĐ 1', rateLabel: '48.167 đ/tháng (~578k/năm)' },
      { yearLabel: 'Năm HĐ 2 - 12', rateLabel: 'Tăng 2.000 đ/tháng mỗi năm' },
      { yearLabel: '🎯 Từ Năm HĐ 13 trở đi', rateLabel: 'Khống chế tối đa 70.000 đ/tháng (840k/năm)', isZeroFee: true },
    ],
  },
  coi: {
    key: 'coi',
    title: 'Chi Phí Bảo Hiểm Rủi Ro (COI)',
    category: 'Chi Phí Khấu Trừ',
    badgeColor: '#EF4444',
    shortSummary: 'Khấu trừ hàng tháng để bảo vệ cho quyền lợi Tử vong/TTTB&VV.',
    description:
      'Chi phí bảo hiểm rủi ro (Cost of Insurance - COI) được khấu trừ hàng tháng từ Giá trị tài khoản để đảm bảo chi trả Số tiền bảo hiểm cho khách hàng khi rủi ro tử vong hoặc thương tật xảy ra. Mức phí được tính dựa trên Giới tính, Tuổi thực tế và Số tiền bảo hiểm rủi ro net.',
  },
  accountValue: {
    key: 'accountValue',
    title: 'Giá Trị Tài Khoản Hợp Đồng (AV)',
    category: 'Tích Lũy Tài Sản',
    badgeColor: '#00A651',
    shortSummary: 'Tổng số tiền tích lũy và hưởng lãi trong hợp đồng.',
    description:
      'Giá trị tài khoản hợp đồng là tổng toàn bộ số tiền đã tích lũy trong hợp đồng bao gồm các khoản Phí phân bổ thực tế cộng Lãi suất đầu tư hàng tháng trừ đi các chi phí khấu trừ hàng tháng. Đây là tài sản tích lũy thực tế của khách hàng.',
    notes: [
      'Bảng hiển thị 2 kịch bản: Lãi suất minh họa dự kiến (ví dụ 4.0%) và Lãi suất cam kết tối thiểu.',
      'Khách hàng có thể linh hoạt rút tiền hoặc vay từ Giá trị tài khoản.',
    ],
  },
  surrenderValue: {
    key: 'surrenderValue',
    title: 'Giá Trị Hoàn Lại (Surrender Value - SV)',
    category: 'Tích Lũy & Hủy HĐ',
    badgeColor: '#8B5CF6',
    shortSummary: 'Số tiền khách hàng nhận được khi chấm dứt HĐ trước hạn.',
    description:
      'Giá trị hoàn lại là số tiền Khách hàng được nhận khi quyết định yêu cầu chấm dứt/hủy bỏ hợp đồng trước khi hết hạn. Giá trị hoàn lại bằng Giá trị tài khoản trừ đi Phí chấm dứt hợp đồng (Phí hủy).',
    feeSchedule: [
      { yearLabel: 'Năm HĐ 1', rateLabel: 'Phí hủy 100% (GTHL = 0 đ)' },
      { yearLabel: 'Năm HĐ 2', rateLabel: 'Phí hủy 90%' },
      { yearLabel: 'Năm HĐ 3', rateLabel: 'Phí hủy 45%' },
      { yearLabel: 'Năm HĐ 4', rateLabel: 'Phí hủy 30%' },
      { yearLabel: 'Năm HĐ 5', rateLabel: 'Phí hủy 20%' },
      { yearLabel: 'Năm HĐ 6', rateLabel: 'Phí hủy 10%' },
      { yearLabel: '🎯 Từ Năm HĐ 7 trở đi', rateLabel: '0% PHÍ HỦY — GTHL BẰNG 100% GTTK!', isZeroFee: true },
    ],
    notes: [
      'Từ năm thứ 7 trở đi, khi hủy hợp đồng khách hàng nhận lại trọn vẹn 100% Giá trị tài khoản.',
    ],
  },
  deathBenefit: {
    key: 'deathBenefit',
    title: 'Quyền Lợi Tử Vong / TTTB&VV',
    category: 'Bảo Vệ Sinh Mạng',
    badgeColor: '#E11D48',
    shortSummary: 'Số tiền bảo vệ chi trả khi biến cố rủi ro lớn xảy ra.',
    description:
      'Số tiền bồi thường bảo hiểm chi trả cho Người thụ hưởng nếu Người được bảo hiểm không may tử vong hoặc Thương tật toàn bộ & vĩnh viễn. Giá trị bằng Số tiền bảo hiểm sản phẩm chính cộng với Giá trị tài khoản tích lũy.',
  },
  loyaltyBonus: {
    key: 'loyaltyBonus',
    title: 'Quyền Lợi Duy Trì Hợp Đồng (Thưởng)',
    category: 'Thưởng Khách Hàng',
    badgeColor: '#10B981',
    shortSummary: 'Tiền thưởng cộng thẳng vào tài khoản để tri ân khách hàng.',
    description:
      'Khoản thưởng tài chính Manulife tự động cộng thêm trực tiếp vào Giá trị tài khoản hợp đồng để tri ân các khách hàng đóng phí đầy đủ và duy trì hợp đồng lâu dài tại các mốc năm hợp đồng quy định (ví dụ Năm 3, 6, 9, 12, 15, 18, 20).',
  },
  basePremium: {
    key: 'basePremium',
    title: 'Phí Bảo Hiểm Cơ Bản',
    category: 'Đóng Phí',
    badgeColor: '#00A651',
    shortSummary: 'Phí bảo hiểm chính đóng hàng năm theo Hợp đồng.',
    description:
      'Phí bảo hiểm cơ bản là số tiền đóng hàng năm cho sản phẩm chính để duy trì quyền lợi bảo vệ sinh mạng và tích lũy tài sản ban đầu.',
  },
  allocatedBasePremium: {
    key: 'allocatedBasePremium',
    title: 'Phí Cơ Bản Phân Bổ (Tiền Đi Đầu Tư)',
    category: 'Tích Lũy Đầu Tư',
    badgeColor: '#00A651',
    shortSummary: 'Số tiền cơ bản thực tế đưa vào tài khoản sau khi trừ Phí Ban Đầu.',
    description:
      'Số tiền còn lại của Phí bảo hiểm cơ bản sau khi đã trừ đi Phí ban đầu tương ứng theo năm hợp đồng. Khoản tiền này được đưa trực tiếp vào Giá trị tài khoản cơ bản để đầu tư sinh lãi.',
  },
  allocatedPremium: {
    key: 'allocatedPremium',
    title: 'Tổng Phí Phân Bổ Đi Đầu Tư',
    category: 'Tích Lũy Đầu Tư',
    badgeColor: '#00A651',
    shortSummary: 'Tổng số tiền phân bổ thực tế vào tài khoản tích lũy sinh lãi.',
    description:
      'Tổng khoản tiền thực tế từ Phí cơ bản sau khấu trừ và 100% Phí đóng thêm được phân bổ vào các tài khoản tích lũy hợp đồng.',
  },
  totalDeduction: {
    key: 'totalDeduction',
    title: 'Tổng Chi Phí Khấu Trừ Hàng Tháng',
    category: 'Chi Phí Khấu Trừ',
    badgeColor: '#EF4444',
    shortSummary: 'Tổng các khoản phí tự động khấu trừ từ tài khoản hàng tháng.',
    description:
      'Tổng các chi phí được trừ tự động hàng tháng từ Giá trị tài khoản hợp đồng, bao gồm Chi phí bảo hiểm rủi ro (COI) và Phí quản lý hợp đồng (Policy Fee).',
  },
  stbhMainProduct: {
    key: 'stbhMainProduct',
    title: 'Số Tiền Bảo Hiểm Sản Phẩm Chính',
    category: 'Mức Bảo Vệ',
    badgeColor: '#2563EB',
    shortSummary: 'Hạn mức bảo vệ tài chính cố định của sản phẩm chính.',
    description:
      'Số tiền bảo hiểm (STBH) là hạn mức cam kết bồi thường tối thiểu của sản phẩm chính khi biến cố rủi ro thuộc phạm vi bảo vệ xảy ra.',
  },
  baseAccountValue: {
    key: 'baseAccountValue',
    title: 'Giá Trị Tài Khoản Cơ Bản',
    category: 'Tài Sản Tích Lũy',
    badgeColor: '#059669',
    shortSummary: 'Tài khoản tích lũy sinh từ Phí cơ bản hàng năm.',
    description:
      'Giá trị tích lũy hình thành từ nguồn Phí bảo hiểm cơ bản sau khấu trừ ban đầu, được tích lũy cộng lãi suất hàng tháng và khấu trừ các chi phí duy trì hợp đồng.',
  },
  topUpAccountValue: {
    key: 'topUpAccountValue',
    title: 'Giá Trị Tài Khoản Đóng Thêm',
    category: 'Tài Sản Tích Lũy',
    badgeColor: '#0284C7',
    shortSummary: 'Tài khoản tích lũy sinh từ khoản Phí đầu tư thêm.',
    description:
      'Giá trị tài sản tích lũy độc lập hình thành từ khoản Phí đóng thêm (Top-Up) của khách hàng. Tài khoản này được hưởng 100% lãi suất đầu tư và linh hoạt rút tiền.',
  },
  interestEarned: {
    key: 'interestEarned',
    title: 'Tiền Lãi Đầu Tư Tích Lũy Trong Năm',
    category: 'Tăng Trưởng Tài Sản',
    badgeColor: '#16A34A',
    shortSummary: 'Tiền lãi đầu tư được cộng thêm vào tài khoản hàng tháng.',
    description:
      'Tổng số tiền lãi đầu tư tích lũy thực tế hoặc minh họa thu được từ việc công ty quản lý quỹ áp dụng mức lãi suất công bố/cam kết vào Giá trị tài khoản trung bình hàng tháng.',
  },
  withdrawalTotal: {
    key: 'withdrawalTotal',
    title: 'Rút Giá Trị Tài Khoản (Withdrawal)',
    category: 'Rút Tiền Linh Hoạt',
    badgeColor: '#3B82F6',
    shortSummary: 'Số tiền rút bớt từ Giá trị tài khoản hợp đồng cho các nhu cầu tài chính cá nhân.',
    description:
      'Khách hàng có thể chủ động rút một phần Giá trị tài khoản hợp đồng bất kỳ lúc nào kể từ khi hợp đồng có hiệu lực để trang trải các nhu cầu tài chính cá nhân (học vấn cho con, mua sắm, tích lũy, hưu trí). Khoản tiền rút sẽ làm giảm tương ứng Giá trị tài khoản hợp đồng.',
    feeSchedule: [
      { yearLabel: 'Năm HĐ 1 - 2', rateLabel: 'Khách hàng có thể rút từ GTTK đóng thêm' },
      { yearLabel: '🎯 Từ Năm HĐ 3 trở đi', rateLabel: 'Linh hoạt rút từ cả GTTK cơ bản và GTTK đóng thêm', isZeroFee: true },
    ],
    notes: [
      'Miễn phí hoàn toàn dịch vụ rút tiền từ Giá trị tài khoản đóng thêm.',
      'Việc rút tiền từ Giá trị tài khoản cơ bản có thể ảnh hưởng đến Số tiền bảo hiểm rủi ro.',
    ],
  },
  riderPremium: {
    key: 'riderPremium',
    title: 'Phí Sản Phẩm Bổ Trợ (Riders)',
    category: 'Sản Phẩm Đính Kèm',
    badgeColor: '#7C3AED',
    shortSummary: 'Tổng phí của các giải pháp bảo vệ sức khỏe, tai nạn đính kèm.',
    description:
      'Phí hàng năm của các sản phẩm bổ trợ đính kèm như Sống Khỏe Mỗi Ngày, Lá Chắn Xanh, Dự Phòng Xanh, Hộ Vệ Xanh, Bảo Thân Xanh nhằm gia tăng phạm vi bảo vệ toàn diện cho cả gia đình.',
  },
};

export interface ColumnInfoModalProps {
  visible: boolean;
  columnKey: string | null;
  onClose: () => void;
}

export const resolveColumnInfo = (key: string): ColumnInfoDetails => {
  if (COLUMN_INFO_DATABASE[key]) return COLUMN_INFO_DATABASE[key];

  const lower = key.toLowerCase();
  if (lower.includes('account') || lower.includes('taikhoan') || lower.includes('gtc') || lower.includes('gttk')) {
    return COLUMN_INFO_DATABASE['accountValue'];
  }
  if (lower.includes('surrender') || lower.includes('hoanlai') || lower.includes('gthl')) {
    return COLUMN_INFO_DATABASE['surrenderValue'];
  }
  if (lower.includes('death') || lower.includes('tuvong') || lower.includes('ql tử vong')) {
    return COLUMN_INFO_DATABASE['deathBenefit'];
  }
  if (lower.includes('coi')) {
    return COLUMN_INFO_DATABASE['coi'];
  }
  if (lower.includes('premium') || lower.includes('phi')) {
    return COLUMN_INFO_DATABASE['premiumPaid'];
  }
  if (lower.includes('topup') || lower.includes('daututhem')) {
    return COLUMN_INFO_DATABASE['topUpPaid'];
  }
  if (lower.includes('withdraw') || lower.includes('rut')) {
    return COLUMN_INFO_DATABASE['withdrawalTotal'];
  }

  return {
    key: key,
    title: key,
    category: 'Thông Tin Cột',
    badgeColor: '#00A651',
    shortSummary: 'Chi tiết thông tin cột dữ liệu minh họa dòng tiền.',
    description: 'Thông tin giải thích về chỉ số này trong bảng minh họa dòng tiền hợp đồng bảo hiểm.',
  };
};

export const ColumnInfoModal: React.FC<ColumnInfoModalProps> = ({ visible, columnKey, onClose }) => {
  if (!visible || !columnKey) return null;

  const info = resolveColumnInfo(columnKey);

  const modalContent = (
    <View style={styles.overlay}>
      <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose} />
      <View style={styles.dialogCard}>
        {/* Header */}
        <View style={styles.dialogHeader}>
          <View style={styles.headerTitleRow}>
            <View style={[styles.iconCircle, { backgroundColor: (info.badgeColor || '#00A651') + '15' }]}>
              <Icon name="info" size={20} color={info.badgeColor || '#00A651'} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={styles.dialogTitle}>{info.title}</Text>
              <View style={[styles.badge, { backgroundColor: (info.badgeColor || '#00A651') + '18' }]}>
                <Text style={[styles.badgeText, { color: info.badgeColor || '#00A651' }]}>
                  {info.category}
                </Text>
              </View>
            </View>
          </View>
          <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
            <Icon name="close" size={20} color="#64748b" />
          </TouchableOpacity>
        </View>

        {/* Body content */}
        <ScrollView style={styles.scrollBody} showsVerticalScrollIndicator={true}>
          {/* Short summary banner */}
          <View style={styles.summaryBox}>
            <Text style={styles.summaryText}>{info.shortSummary}</Text>
          </View>

          {/* Main explanation */}
          <Text style={styles.sectionTitle}>💡 Khái niệm & Ý nghĩa</Text>
          <Text style={styles.descParagraph}>{info.description}</Text>

          {/* Fee schedule table if available */}
          {info.feeSchedule && info.feeSchedule.length > 0 && (
            <View style={styles.scheduleSection}>
              <Text style={styles.sectionTitle}>📅 Mốc Thời Gian & Biểu Phí Khấu Trừ</Text>
              <View style={styles.scheduleTable}>
                {info.feeSchedule.map((item, idx) => (
                  <View
                    key={idx}
                    style={[
                      styles.scheduleRow,
                      item.isZeroFee && styles.scheduleRowZeroFee,
                      idx % 2 === 1 && !item.isZeroFee && { backgroundColor: '#f8fafc' },
                    ]}
                  >
                    <Text style={[styles.scheduleYear, item.isZeroFee && styles.textZeroFee]}>
                      {item.yearLabel}
                    </Text>
                    <Text style={[styles.scheduleRate, item.isZeroFee && styles.textZeroFeeBold]}>
                      {item.rateLabel}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {/* Extra notes if any */}
          {info.notes && info.notes.length > 0 && (
            <View style={styles.notesSection}>
              <Text style={styles.sectionTitle}>📌 Lưu ý quan trọng</Text>
              {info.notes.map((note, idx) => (
                <View key={idx} style={styles.noteItem}>
                  <Text style={styles.bulletDot}>•</Text>
                  <Text style={styles.noteText}>{note}</Text>
                </View>
              ))}
            </View>
          )}
        </ScrollView>

        {/* Footer action button */}
        <View style={styles.dialogFooter}>
          <TouchableOpacity style={styles.submitBtn} onPress={onClose}>
            <Text style={styles.submitBtnText}>Đã hiểu</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  if (Platform.OS === 'web') {
    return modalContent;
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      {modalContent}
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.65)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing[4],
    ...(Platform.OS === 'web'
      ? {
          position: 'fixed' as any,
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 999999,
        }
      : {}),
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  dialogCard: {
    width: '100%',
    maxWidth: 520,
    maxHeight: '85%',
    backgroundColor: '#ffffff',
    borderRadius: borderRadius.lg,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.25,
    shadowRadius: 20,
    elevation: 10,
    zIndex: 1000000,
  },
  dialogHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: spacing[4],
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  headerTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
    flex: 1,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: 16,
    fontWeight: fontWeights.bold,
    color: '#0f172a',
    marginBottom: 4,
    fontFamily: fonts.headline,
  },
  badge: {
    alignSelf: 'flex-start',
    paddingHorizontal: spacing[2],
    paddingVertical: 2,
    borderRadius: borderRadius.full,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: fontWeights.bold,
  },
  closeBtn: {
    padding: spacing[1],
  },
  scrollBody: {
    padding: spacing[4],
  },
  summaryBox: {
    backgroundColor: '#f0fdf4',
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    borderRadius: borderRadius.sm,
    padding: spacing[3],
    marginBottom: spacing[4],
  },
  summaryText: {
    fontSize: 13,
    color: '#166534',
    fontWeight: fontWeights.medium,
    lineHeight: 18,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: fontWeights.bold,
    color: '#1e293b',
    marginBottom: spacing[2],
    marginTop: spacing[2],
  },
  descParagraph: {
    fontSize: 13,
    color: '#475569',
    lineHeight: 20,
    marginBottom: spacing[4],
  },
  scheduleSection: {
    marginBottom: spacing[4],
  },
  scheduleTable: {
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: borderRadius.md,
    overflow: 'hidden',
  },
  scheduleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: spacing[2.5],
    paddingHorizontal: spacing[3],
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  scheduleRowZeroFee: {
    backgroundColor: '#dcfce7',
    borderBottomColor: '#bbf7d0',
  },
  scheduleYear: {
    fontSize: 12.5,
    color: '#334155',
    fontWeight: fontWeights.medium,
  },
  scheduleRate: {
    fontSize: 12.5,
    color: '#0f172a',
    fontWeight: fontWeights.bold,
    fontFamily: fonts.mono,
    fontVariant: ['tabular-nums'],
  },
  textZeroFee: {
    color: '#15803d',
    fontWeight: fontWeights.bold,
  },
  textZeroFeeBold: {
    color: '#15803d',
    fontWeight: fontWeights.bold,
  },
  notesSection: {
    marginBottom: spacing[3],
    backgroundColor: '#fffbeb',
    padding: spacing[3],
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: '#fef3c7',
  },
  noteItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing[2],
    marginTop: spacing[1],
  },
  bulletDot: {
    fontSize: 14,
    color: '#b45309',
  },
  noteText: {
    flex: 1,
    fontSize: 12,
    color: '#92400e',
    lineHeight: 17,
  },
  dialogFooter: {
    padding: spacing[4],
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
    backgroundColor: '#f8fafc',
    alignItems: 'flex-end',
  },
  submitBtn: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing[6],
    paddingVertical: spacing[2.5],
    borderRadius: borderRadius.md,
  },
  submitBtnText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: fontWeights.bold,
  },
});
